import { useState } from 'react';
import { ThumbsUp, Check, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Modal, LimitReachedModal } from '../ui';
import { useTranslation } from '../../context/LanguageContext';
import { addSkillEndorsement } from '../../services/skillsService';
import { etatRecommander } from '../../utils/recommandationsCompetences';
import { lireRefusFonction } from '../../utils/refusQuota';

/**
 * À côté d'une compétence de la fiche : le nombre de recommandations acceptées
 * et, pour un membre connecté, le geste « Recommander ».
 *
 * @param {Object} info - Entrée de indexerRecommandations pour cette compétence
 * @param {string} mode - Mode de réception du membre (ask | auto | none)
 * @param {boolean} estProprietaire
 * @param {boolean} connecte
 * @param {boolean} viewerAllowed - Faux : la fonction est reservee aux abonnements payants, le clic ouvre l'offre
 * @param {string} skillName
 * @param {string} ownerName - Prénom du membre recommandé
 * @param {Function} onChanged - Recharger la fiche après une recommandation
 */
const SkillEndorsement = ({ info, mode, estProprietaire, connecte, viewerAllowed = true, skillName, ownerName, onChanged }) => {
    const { t } = useTranslation();
    const l = t.skillEndorsements || {};

    const [ouvert, setOuvert] = useState(false);
    const [commentaire, setCommentaire] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [erreur, setErreur] = useState('');
    const [resultat, setResultat] = useState('');
    const [reservee, setReservee] = useState(false);
    const navigate = useNavigate();

    if (!info) return null;

    const etat = etatRecommander({ mode, mine: info.mine, estProprietaire, connecte });
    const noms = info.auteurs.map(a => a.endorserName).filter(Boolean).join(', ');

    const fermer = () => {
        setOuvert(false);
        setCommentaire('');
        setErreur('');
        if (resultat) onChanged?.();
        setResultat('');
    };

    const envoyer = async () => {
        setEnvoi(true);
        setErreur('');
        try {
            const res = await addSkillEndorsement(info.peoplesSkillId, commentaire);
            if (res.success) {
                setResultat(res.status === 'accepted' ? 'accepted' : 'pending');
                return;
            }
            if (lireRefusFonction(res)) {
                setOuvert(false);
                setReservee(true);
                return;
            }
            const messages = {
                ENDORSEMENTS_REFUSED: l.errorRefused,
                ALREADY_ENDORSED: l.errorAlready,
                OWN_SKILL: l.errorOwn
            };
            setErreur(messages[res.code] || l.errorGeneric || '');
        } finally {
            setEnvoi(false);
        }
    };

    return (
        <>
            {info.count > 0 && (
                <span
                    className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full bg-purple-50 text-cabaza-purple text-xs font-medium align-middle"
                    title={noms ? `${l.recommendedBy || ''} ${noms}` : undefined}
                    data-testid="competence-recommandations"
                >
                    <ThumbsUp className="w-3 h-3" />
                    {info.count}
                </span>
            )}

            {etat === 'available' && (
                <button
                    type="button"
                    onClick={() => (viewerAllowed ? setOuvert(true) : setReservee(true))}
                    className="ml-2 text-xs text-cabaza-purple hover:underline align-middle"
                >
                    {l.recommend || 'Recommander'}
                </button>
            )}
            {etat === 'pending' && (
                <span className="inline-flex items-center gap-1 ml-2 text-xs text-gray-500 align-middle">
                    <Clock className="w-3 h-3" /> {l.pendingLabel || 'Envoyée, en attente'}
                </span>
            )}
            {etat === 'accepted' && (
                <span className="inline-flex items-center gap-1 ml-2 text-xs text-emerald-600 align-middle">
                    <Check className="w-3 h-3" /> {l.youRecommend || 'Vous recommandez'}
                </span>
            )}

            {reservee && (
                <LimitReachedModal
                    isOpen
                    feature
                    onClose={() => setReservee(false)}
                    moduleCode="CONTACTS"
                    profileTypeId={parseInt(localStorage.getItem('userProfileTypeId'), 10)}
                    peopleUuid={localStorage.getItem('userUuid')}
                    onNavigate={(page, state) => { setReservee(false); navigate('/', { state: { page, tabName: state?.tab } }); }}
                />
            )}

            {ouvert && (
                <Modal
                    isOpen
                    onClose={fermer}
                    title={(l.recommendTitle || 'Recommander {skill}').replace('{skill}', skillName)}
                    icon={ThumbsUp}
                    size="md"
                    onSubmit={resultat ? fermer : envoyer}
                    submitText={resultat ? (l.close || 'Fermer') : (l.send || 'Envoyer')}
                    loading={envoi}
                >
                    {resultat ? (
                        <p className="text-gray-800 py-4" data-testid="recommandation-envoyee">
                            {(resultat === 'accepted' ? l.sentAccepted : l.sentPending || '').replace('{name}', ownerName)}
                        </p>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                {((mode === 'auto' ? l.introAuto : l.introAsk) || '')
                                    .replace('{skill}', skillName)
                                    .replace(/\{name\}/g, ownerName)}
                            </p>
                            {erreur && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" role="alert">{erreur}</div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{l.commentLabel}</label>
                                <textarea
                                    value={commentaire}
                                    onChange={(e) => setCommentaire(e.target.value)}
                                    maxLength={500}
                                    rows={3}
                                    placeholder={l.commentPlaceholder}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </>
    );
};

export default SkillEndorsement;
